import { Document } from "@models";
import { IntegrationType } from "@types";
import { INTEGRATIONS } from "../../../../config/integrations";
import { getMcpPresetConfigForConnection } from "../../../../config/mcp-presets";
import { getMcpFaviconUrl } from "../../../../utils/mcp-favicon";
import type { ChatToolMessage } from "../../types";
import { keyToLabel } from "./utils";

export enum ToolCallName {
  EDIT_DATA_KEY = "edit_data_key",
  LEGACY_WRITE_DATA_KEY = "write_data_key",
  LIST_DATA_KEYS = "list_data_keys",
  READ_DATA_KEY = "read_data_key",
  READ_DOCUMENT = "read_document",
  READ_DOCUMENT_METADATA = "read_document_metadata",
  EDIT_DOCUMENT = "edit_document",
  GET_DEVICE = "get_device",
  GREP_SEARCH = "grep_search",
  THINK = "think",
  EDIT_QUALITY_EVENT_RECORD = "edit_quality_event_record",
  DRAFT_SKILL = "draft_skill",
  DRAFT_TRAINING_PROGRAM = "draft_training_program",
  DRAFT_TRAINING_QUIZ = "draft_training_quiz",
  ADD_USER_TO_FUNCTIONAL_GROUP = "add_user_to_functional_group",
  TASK = "task",
  READ_DATA_KEY_CONFIGURATION = "read_data_key_configuration",
  WRITE_TODOS = "write_todos",
  EDIT_CUSTOM_DOCUMENT_TEXT = "edit_custom_document_text",
}

export const getDiscardedToolCallIds = (
  metadata: Record<string, any> | undefined
) =>
  Array.isArray(metadata?.discarded_tool_call_ids)
    ? [...new Set(metadata.discarded_tool_call_ids.map(String))]
    : [];

interface ToolConfig {
  expandedByDefault: boolean;
  title?: (
    args: Record<string, any>,
    toolName?: string,
    toolResult?: ChatToolMessage,
    documents?: Document[]
  ) => string;
  showResult?: boolean;
  errorMessage?: string;
  hidden?: boolean;
}

const editDataKeyToolConfig: ToolConfig = {
  expandedByDefault: true, // Always expand to show the edited content
  title: (args, _, toolResult) => {
    if (toolResult && typeof args?.id === "string" && args.id.length > 0) {
      return `Edit ${keyToLabel(args.id)}`;
    }
    return toolResult ? "Edit data key" : "Editing...";
  },
  errorMessage: "Failed to edit data key.",
};

// Tool configurations - Configure display behavior for each tool
export const TOOL_CONFIGS: Record<string, ToolConfig> = {
  [ToolCallName.EDIT_DATA_KEY]: editDataKeyToolConfig,
  [ToolCallName.LEGACY_WRITE_DATA_KEY]: editDataKeyToolConfig,
  [ToolCallName.LIST_DATA_KEYS]: {
    expandedByDefault: false,
    title: () => "Reading data keys",
  },
  [ToolCallName.READ_DATA_KEY]: {
    expandedByDefault: false,
    title: (args) => `Reading data key ${args?.data_key || args?.id || "..."}`,
  },
  [ToolCallName.READ_DOCUMENT]: {
    expandedByDefault: false,
    title: (args, _, __, documents) => {
      const document = documents?.find((doc) => doc.id === args?.id);
      return `Reading ${document?.title || args?.id || "document"}`;
    },
  },
  [ToolCallName.READ_DOCUMENT_METADATA]: {
    expandedByDefault: false,
    title: (args, _, __, documents) => {
      const document = documents?.find((doc) => doc.id === args?.id);
      return `Reading metadata for ${document?.title || args?.id || "document"}`;
    },
  },
  [ToolCallName.EDIT_DOCUMENT]: {
    expandedByDefault: false,
    title: (args, _, __, documents) => {
      const documentId =
        typeof args?.document_id === "string" ? args.document_id : args?.id;
      const document = documents?.find((doc) => doc.id === documentId);
      return `Edited ${document?.title || documentId || "document"}`;
    },
  },
  [ToolCallName.GET_DEVICE]: {
    expandedByDefault: false,
    title: () => "Reading device information",
  },
  [ToolCallName.GREP_SEARCH]: {
    expandedByDefault: false,
    title: (args) => `Searching matches for "${args?.pattern || "..."}"`,
  },
  [ToolCallName.THINK]: {
    hidden: true,
    expandedByDefault: false, // Think calls are always shown directly
    showResult: false,
    title: () => "Saving thoughts",
  },
  [ToolCallName.DRAFT_SKILL]: {
    expandedByDefault: true,
    title: (args) => `Draft skill${args?.name ? `: ${args.name}` : ""}`,
  },
  [ToolCallName.DRAFT_TRAINING_QUIZ]: {
    expandedByDefault: true,
    title: () => "Draft training quiz",
  },
  [ToolCallName.ADD_USER_TO_FUNCTIONAL_GROUP]: {
    expandedByDefault: false,
    title: (args) => {
      const functionalGroupName =
        typeof args?.functionalGroupName === "string"
          ? args.functionalGroupName.trim()
          : "";
      return `Would you like to join the ${functionalGroupName || "training administration"} functional group?`;
    },
  },
  [ToolCallName.DRAFT_TRAINING_PROGRAM]: {
    expandedByDefault: true,
    title: () => "Edit training program",
  },
  [ToolCallName.TASK]: {
    expandedByDefault: true,
    title: (args) => {
      const subagentType =
        typeof args?.subagent_type === "string" ? args.subagent_type : null;
      return subagentType
        ? `Delegating to ${keyToLabel(subagentType)}`
        : "Delegating to subagent";
    },
  },
  [ToolCallName.WRITE_TODOS]: {
    hidden: false,
    expandedByDefault: false,
    showResult: false,
    title: () => "Updating todos",
  },
  [ToolCallName.READ_DATA_KEY_CONFIGURATION]: {
    expandedByDefault: false,
    showResult: false,
    title: (args) => `Reading config ${args?.id ? `for ${args.id}` : "..."}`,
  },
  [ToolCallName.EDIT_CUSTOM_DOCUMENT_TEXT]: {
    expandedByDefault: true,
    title: (args, _, __, documents) => {
      const document = documents?.find((doc) => doc.id === args?.document_id);
      const documentTitle = document?.title || args?.document_id || "...";
      return `Suggested edit for ${documentTitle}`;
    },
  },
};

// Default configuration for unknown tools
export const DEFAULT_TOOL_CONFIG: ToolConfig = {
  expandedByDefault: false,
  title: (_: Record<string, any>, toolName?: string) =>
    keyToLabel(toolName || ""),
};

// Map tool name prefixes to integration types
const INTEGRATION_PREFIXES: Record<string, IntegrationType> = {
  linear: IntegrationType.LINEAR,
  github: IntegrationType.GITHUB,
  jira: IntegrationType.JIRA,
  slack: IntegrationType.SLACK,
};

export interface CustomMcpConnectionIconContext {
  id: string;
  name: string;
  serverUrl: string;
  presetKey?: string | null;
}

function slugifyCustomConnectionName(name: string, fallback: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "") || fallback
  ).slice(0, 40);
}

function getCustomMcpToolPrefix(
  connection: CustomMcpConnectionIconContext
): string {
  return slugifyCustomConnectionName(
    connection.name,
    `mcp_${connection.id.slice(0, 8)}`
  );
}

function getCustomMcpIcon(
  toolName: string,
  customConnections: CustomMcpConnectionIconContext[]
): string | null {
  for (const connection of customConnections) {
    const prefix = getCustomMcpToolPrefix(connection);
    if (!toolName.startsWith(`${prefix}_`)) {
      continue;
    }

    const presetConfig = getMcpPresetConfigForConnection(connection);
    if (presetConfig) {
      return presetConfig.card.logo;
    }

    return getMcpFaviconUrl(connection.serverUrl);
  }

  return null;
}

/**
 * Returns the integration logo URL for a tool name, or null if the tool
 * does not belong to a known integration.
 * Tool names from integrations follow the pattern `{integration}_{action}`.
 */
export function getToolIntegrationIcon(
  toolName: string,
  customConnections?: CustomMcpConnectionIconContext[]
): string | null {
  const prefix = toolName.split("_")[0];
  const integrationType = INTEGRATION_PREFIXES[prefix];
  if (integrationType) {
    return INTEGRATIONS[integrationType]?.logo ?? null;
  }

  if (customConnections?.length) {
    return getCustomMcpIcon(toolName, customConnections);
  }

  return null;
}

/** Remove the server prefix that our MCP loader adds to tool names. */
export function getMcpServerToolName(
  toolName: string,
  connections: CustomMcpConnectionIconContext[] = []
): string {
  const prefixes = [
    ...connections.map(getCustomMcpToolPrefix),
    ...Object.keys(INTEGRATION_PREFIXES),
  ].sort((a, b) => b.length - a.length);
  const prefix = prefixes.find((value) => toolName.startsWith(`${value}_`));
  return prefix ? toolName.slice(prefix.length + 1) : toolName;
}
